import { logout } from "@/api/auth";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import {
  Gear,
  House,
  List,
  MagnifyingGlass,
  Moon,
  SignOut,
  Sun,
  UserCircle,
  X,
} from "@phosphor-icons/react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NotificationBell } from "../NotificationBell";
import { CountBadge } from "../ui/CountBadge";
import { Drawer } from "../ui/Drawer";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";
import { NotificationDot } from "../ui/NotificationDot";
import { Popover } from "../ui/Popover";
import { Tooltip } from "../ui/Tooltip";

export function Navbar() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, toggleTheme] = useTheme();

  const handleLogout = async () => {
    await logout();
    await auth.refresh();
    navigate("/");
  };

  const navLinks = [
    { to: "/playground", label: "Playground" },
    { to: "/activity", label: "Activity" },
    { to: "/issues", label: "Issues" },
    { to: "/changelog", label: "Changelog" },
    { to: "/docs", label: "API Docs" },
    { to: "/contributors", label: "Contributors" },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-bg-card/90 backdrop-blur-sm">
      <nav
        aria-label="Main"
        className="section-container flex items-center justify-between h-14 gap-4"
      >
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 font-brand font-bold text-primary-dark tracking-wide no-underline hover:opacity-80 transition-opacity"
        >
          <svg className="w-6 h-7" viewBox="0 0 52.6 64" aria-hidden="true">
            <defs>
              <linearGradient
                id="kb-t"
                x1="19"
                y1="2"
                x2="52"
                y2="38"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#1d4fff" />
                <stop offset=".52" stopColor="#2f64ff" />
                <stop offset="1" stopColor="#6848ff" />
              </linearGradient>
              <linearGradient
                id="kb-b"
                x1="6"
                y1="30"
                x2="51"
                y2="64"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#2155ff" />
                <stop offset=".48" stopColor="#344dff" />
                <stop offset="1" stopColor="#5c35f2" />
              </linearGradient>
            </defs>
            <path
              fill="url(#kb-t)"
              d="M37.4 5.5 51.8 0v18.2L31.5 38.5l-9.8-9.8 15.7-15.7V5.5Z"
            />
            <path
              fill="url(#kb-b)"
              d="M11.5 31.4 28.2 48.1 51.8 24.5v16.9L28.2 64 0.8 36.7l10.7-5.3Z"
            />
          </svg>
          <span className="text-text-muted text-xs font-body font-normal normal-case tracking-normal">
            KuberCoin /
          </span>
          <span className="text-sm">open-source</span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden sm:flex items-center gap-2">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                aria-current={active ? "page" : undefined}
                className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "text-primary"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
          <a
            href="https://kuber-coin.com"
            className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-text-muted transition-colors hover:text-text-primary"
          >
            <House size={15} />
            kuber-coin.com
          </a>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <Tooltip content="Search" placement="bottom">
            <Link
              to="/search"
              className="p-2 rounded-lg text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors"
              aria-label="Search"
            >
              <MagnifyingGlass size={18} />
            </Link>
          </Tooltip>
          <Tooltip
            content={theme === "light" ? "Dark mode" : "Light mode"}
            placement="bottom"
          >
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors"
              aria-label={
                theme === "light"
                  ? "Switch to dark mode"
                  : "Switch to light mode"
              }
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
          </Tooltip>
          <LanguageSwitcher />

          {auth.role === "developer" && (
            <span className="relative">
              <NotificationDot show={false}>
                <NotificationBell />
              </NotificationDot>
              <CountBadge count={0} className="absolute -top-1 -right-1" />
            </span>
          )}

          {auth.role === "guest" && (
            <Link
              to="/login"
              className="text-sm px-3 py-1.5 rounded-lg bg-primary-muted text-primary-light font-medium transition-colors hover:bg-primary hover:text-white"
            >
              Dev login
            </Link>
          )}

          {auth.role === "developer" && (
            <Popover
              trigger={
                <span className="hidden sm:flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-text-secondary transition-colors hover:text-text-primary cursor-pointer">
                  <UserCircle size={16} />
                  {auth.name?.split(" ")[0]}
                </span>
              }
              content={
                <div className="space-y-2 min-w-40">
                  <Link
                    to="/dev-portal"
                    className="block px-3 py-1.5 text-sm rounded hover:bg-bg-surface"
                  >
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-3 py-1.5 text-sm rounded hover:bg-bg-surface"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-bg-surface text-red-400"
                  >
                    Log out
                  </button>
                </div>
              }
              placement="bottom"
            />
          )}

          {auth.role === "admin" && (
            <>
              <Link
                to="/open-admin"
                className="hidden sm:flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-text-secondary transition-colors hover:text-text-primary"
              >
                <Gear size={16} />
                Admin
              </Link>
              <button
                onClick={handleLogout}
                className="hidden sm:block p-2 rounded-lg text-text-muted transition-colors hover:bg-bg-surface"
                aria-label="Log out"
              >
                <SignOut size={18} />
              </button>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="sm:hidden p-2 rounded-lg text-text-muted hover:bg-bg-surface transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        side="left"
        title="Menu"
      >
        <div className="space-y-1">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                aria-current={active ? "page" : undefined}
                className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? "text-primary bg-primary/5"
                    : "text-text-muted hover:bg-bg-surface hover:text-text-primary"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
          <a
            href="https://kuber-coin.com"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors"
          >
            <House size={15} />
            kuber-coin.com
          </a>

          {auth.role === "developer" && (
            <>
              <div className="border-t border-border my-2" />
              <Link
                to="/dev-portal"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors"
              >
                <UserCircle size={16} />
                {auth.name?.split(" ")[0]}
              </Link>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  void handleLogout();
                }}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors text-left"
              >
                <SignOut size={16} />
                Log out
              </button>
            </>
          )}

          {auth.role === "admin" && (
            <>
              <div className="border-t border-border my-2" />
              <Link
                to="/open-admin"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors"
              >
                <Gear size={16} />
                Admin
              </Link>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  void handleLogout();
                }}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors text-left"
              >
                <SignOut size={16} />
                Log out
              </button>
            </>
          )}
        </div>
      </Drawer>
    </header>
  );
}
